import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { PanelRightClose, PanelRightOpen, X } from 'lucide-react';

type WorkflowRightSidebarProps = {
    children: React.ReactNode;
    className?: string;
    mode?: 'inline' | 'overlay';
    title?: string;
    defaultOpen?: boolean;
};

export const WorkflowRightSidebar: React.FC<WorkflowRightSidebarProps> = ({
    children,
    className,
    mode = 'inline',
    title = 'Workflow & Status',
    defaultOpen = false,
}) => {
    const [open, setOpen] = useState(defaultOpen);

    if (mode === 'overlay') {
        return (
            <>
                <button
                    type="button"
                    onClick={() => setOpen((prev) => !prev)}
                    className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-full border bg-white/95 px-3 py-2 text-xs font-medium text-slate-700 shadow-sm backdrop-blur hover:bg-white"
                    aria-label={open ? 'Close workflow panel' : 'Open workflow panel'}
                >
                    {open ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                    {title}
                </button>

                <div
                    className={cn(
                        'fixed inset-0 z-40 bg-slate-900/30 transition-opacity',
                        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    )}
                    onClick={() => setOpen(false)}
                />

                <aside
                    className={cn(
                        'fixed right-0 top-0 z-50 h-full w-full max-w-[360px] overflow-y-auto border-l bg-white p-4 shadow-2xl transition-transform',
                        open ? 'translate-x-0' : 'translate-x-full',
                        className
                    )}
                    aria-hidden={!open}
                >
                    <div className="mb-4 flex items-center justify-between border-b pb-2">
                        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="rounded border px-2 py-1 text-slate-600 hover:bg-slate-50"
                            aria-label="Close workflow panel"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="space-y-6 pb-8">{children}</div>
                </aside>
            </>
        );
    }

    return <aside className={cn('space-y-6 xl:sticky xl:top-6 h-fit', className)}>{children}</aside>;
};
